import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { CSSProperties, Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Outlet,
  generatePath,
  matchPath,
  useLocation,
  useMatch,
} from 'react-router-dom';
import styled from 'styled-components';

import { Navigate, catchAll, useTypedParams } from '@sorare/routing';

import { So5State, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  MLB_PLAY_PRO_FIXTURE_CHASE,
  MLB_PLAY_PRO_FIXTURE_CHASE_USERGROUP_USER,
  MLB_PLAY_PRO_FIXTURE_COMPETITIONS,
  MLB_PLAY_PRO_FIXTURE_GAMES,
  MLB_PLAY_PRO_FIXTURE_GAMES_ID,
  MLB_PLAY_PRO_FIXTURE_MY_LINEUPS,
  MLB_PLAY_PRO_FIXTURE_POSTSEASON,
} from '@sorare/core/src/constants/__generated__/routes';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FixtureSelector } from '@sorare/gameplay/src/components/fixture/FixtureSelector';
import { getFixtureQueryVariables } from '@sorare/gameplay/src/lib/so5';

import {
  BaseballPlayProFixtureLayoutQuery,
  BaseballPlayProFixtureLayoutQueryVariables,
} from './__generated__/layout.graphql';
import { LayoutParams } from './__generated__/routeParams';
import { chaseGameWeeks } from './chaseGameWeeks';
import logoPostseasonWithRewards from './postseason2024withpp.png';
import logoPostseasonMobile from './postseasonLogoMobile.png';

const MLB_GET_COMPETITIONS = gql`
  query BaseballPlayProFixtureLayoutQuery(
    $type: So5State
    $slug: String
    $withTraining: Boolean!
  ) {
    so5 {
      so5Fixture(type: $type, slug: $slug, sport: BASEBALL) {
        slug
        gameWeek
        mySo5LineupsCount(training: $withTraining)
        ...FixtureSelector_so5Fixture
      }
    }
  }
  ${FixtureSelector.fragments.so5Fixture}
` as TypedDocumentNode<
  BaseballPlayProFixtureLayoutQuery,
  BaseballPlayProFixtureLayoutQueryVariables
>;

const PostseasonButton = styled(ButtonBase)`
  border: 2px solid var(--c-nd-200);
  border-radius: var(--double-unit);
  margin: var(--unit);
  overflow: hidden;

  transition: transform 0.1s ease-out;
  &:hover {
    border: 2px solid var(--c-nd-400);
    transform: scale(1.05);
  }
  &.selected {
    border: 2px solid var(--c-nd-600);
  }

  margin-left: calc(-1 * var(--double-unit));

  @media ${tabletAndAbove} {
    margin-left: 0;
  }
`;

const PostseasonLogo = styled.img`
  height: 80px;

  aspect-ratio: 500/234;
  max-width: 170px;

  @media ${tabletAndAbove} {
    aspect-ratio: 1178/255;
    min-width: 370px;
  }
  overflow: hidden;
`;

const FixtureSelectorWrapper = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  padding: var(--double-unit);

  @media ${tabletAndAbove} {
    max-width: 640px;
  }

  &.useMlbPostseasonLobby {
    max-width: calc(100vw - 174px);

    @media ${tabletAndAbove} {
      max-width: 280px;
    }
  }
`;

const PlayProLayout = () => {
  const { pathname } = useLocation();
  const { fixture: fixturePath } = useTypedParams<LayoutParams>();
  const { track } = useEventsContext();
  const isTablet = useIsTabletAndAbove();
  const fixtureData = getFixtureQueryVariables(fixturePath);
  const matchPostseason = useMatch(catchAll(MLB_PLAY_PRO_FIXTURE_POSTSEASON));
  const { lifecycle } = useLifecycle();

  const isTabletAndAbove = useIsTabletAndAbove();
  const {
    flags: { useChaseLobbyUi = false, useMlbPostseasonLobby = false },
  } = useFeatureFlags();

  const { data, error, loading } = useQuery(MLB_GET_COMPETITIONS, {
    variables: {
      ...fixtureData,
      withTraining: !!lifecycle?.showTrainingLineups,
    },
  });

  const { so5 } = data || {};
  const { so5Fixture } = so5 || {};
  const fixtureNotFound = error?.graphQLErrors?.some(
    graphQlError => graphQlError.extensions?.code === 'NOT_FOUND'
  );

  if (fixtureNotFound) {
    return (
      <Navigate
        replace
        to={generatePath(MLB_PLAY_PRO_FIXTURE_MY_LINEUPS, {
          fixture: 'ongoing',
        })}
      />
    );
  }

  // Fallback if fixture does not exist (f.e there is no ongoing or live fixture)
  if (
    !loading &&
    !data?.so5?.so5Fixture &&
    fixtureData.type !== So5State.PAST
  ) {
    return (
      <Navigate
        replace
        to={generatePath(MLB_PLAY_PRO_FIXTURE_MY_LINEUPS, {
          fixture: fixtureData.type === So5State.LIVE ? 'past' : 'live',
        })}
      />
    );
  }

  if (!so5Fixture) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <PageHeader text={glossary.pro} />
      <ContentWithStickyHeader
        header={
          <FullWidthContainer.NoPadding>
            <Horizontal center>
              <FixtureSelectorWrapper
                className={classNames({ useMlbPostseasonLobby })}
              >
                <FixtureSelector
                  withMask
                  currentFixture={so5Fixture}
                  disableLiveAndPast={false}
                  navigateTo={fixture => {
                    const activeRoute = [
                      MLB_PLAY_PRO_FIXTURE_MY_LINEUPS,
                      MLB_PLAY_PRO_FIXTURE_GAMES,
                      MLB_PLAY_PRO_FIXTURE_COMPETITIONS,
                    ].find(route => matchPath(catchAll(route), pathname));

                    return generatePath(
                      activeRoute || MLB_PLAY_PRO_FIXTURE_MY_LINEUPS,
                      { fixture }
                    );
                  }}
                />
              </FixtureSelectorWrapper>
              {useMlbPostseasonLobby && (
                <PostseasonButton
                  to={generatePath(MLB_PLAY_PRO_FIXTURE_POSTSEASON, {
                    fixture: 'baseball-1-3-oct-2024',
                  })}
                  className={classNames({ selected: matchPostseason })}
                >
                  <PostseasonLogo
                    src={
                      isTablet
                        ? logoPostseasonWithRewards
                        : logoPostseasonMobile
                    }
                  />
                </PostseasonButton>
              )}
            </Horizontal>
            {!matchPostseason && (
              <TabBar fullWidth variant="flat">
                <Tab
                  to={generatePath(MLB_PLAY_PRO_FIXTURE_MY_LINEUPS, {
                    fixture: so5Fixture.slug,
                  })}
                  matches={[MLB_PLAY_PRO_FIXTURE_MY_LINEUPS]}
                >
                  <FormattedMessage
                    {...(isTabletAndAbove
                      ? navLabels.myLineupsWithNumber
                      : navLabels.lineupsWithNumber)}
                    values={{ nb: so5Fixture?.mySo5LineupsCount || 0 }}
                  />
                </Tab>
                <Tab
                  to={generatePath(MLB_PLAY_PRO_FIXTURE_COMPETITIONS, {
                    fixture: so5Fixture.slug,
                  })}
                  matches={[MLB_PLAY_PRO_FIXTURE_COMPETITIONS]}
                >
                  <FormattedMessage {...navLabels.competitions} />
                </Tab>
                {useChaseLobbyUi && chaseGameWeeks[so5Fixture.gameWeek] && (
                  <Tab
                    to={generatePath(MLB_PLAY_PRO_FIXTURE_CHASE, {
                      fixture: so5Fixture.slug,
                    })}
                    matches={[
                      MLB_PLAY_PRO_FIXTURE_CHASE,
                      MLB_PLAY_PRO_FIXTURE_CHASE_USERGROUP_USER,
                    ]}
                    onClick={() =>
                      track(
                        `Click ${chaseGameWeeks[so5Fixture.gameWeek]} Lobby Tab`,
                        {
                          sport: Sport.BASEBALL,
                        }
                      )
                    }
                    style={
                      {
                        '--fg-color': 'rgba(240, 206, 29, 0.5)',
                        '--hover-fg-color': 'rgba(240, 206, 29, 0.85)',
                        '--active-fg-color': 'rgba(240, 206, 29, 1)',
                      } as CSSProperties
                    }
                  >
                    {isTabletAndAbove ? (
                      chaseGameWeeks[so5Fixture.gameWeek]
                    ) : (
                      <FormattedMessage {...navLabels.chase} />
                    )}
                  </Tab>
                )}
                <Tab
                  to={generatePath(MLB_PLAY_PRO_FIXTURE_GAMES, {
                    fixture: so5Fixture.slug,
                  })}
                  matches={[
                    MLB_PLAY_PRO_FIXTURE_GAMES,
                    MLB_PLAY_PRO_FIXTURE_GAMES_ID,
                  ]}
                >
                  <FormattedMessage {...glossary.games} />
                </Tab>
              </TabBar>
            )}
          </FullWidthContainer.NoPadding>
        }
      >
        <Suspense fallback={<LoadingIndicator fullHeight />}>
          <Outlet />
        </Suspense>
      </ContentWithStickyHeader>
    </>
  );
};

export default PlayProLayout;
